import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf, Pencil, AlertTriangle } from 'lucide-react';
import { emissionApi } from '../services/emissionApi';
import { EmissionFactor, UpsertEmissionFactorRequest, EMISSION_VEHICLE_TYPES } from '../types/emission';
import EmissionFactorForm from '../components/emission/EmissionFactorForm';
import { ToastContainer } from '../components/common/Toast';
import { useToast } from '../hooks/useToast';
import '../styles/PageLayout.css';

const EmissionFactors: React.FC = () => {
  const { t } = useTranslation(['common']);
  const [factors, setFactors] = useState<EmissionFactor[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<{ value: string; label: string; factor: EmissionFactor | null } | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const { toasts, removeToast, showSuccess, showError } = useToast();

  useEffect(() => { loadFactors(); }, []);

  const loadFactors = async () => {
    try {
      setLoading(true);
      setFactors(await emissionApi.getAll());
    } catch (e) {
      showError(t('common:emissionFactors.loadError', 'Fehler beim Laden der Emissionsfaktoren'));
    } finally {
      setLoading(false);
    }
  };

  const findFactor = (value: string): EmissionFactor | null =>
    factors.find(f => f.vehicleType?.toLowerCase() === value.toLowerCase()) || null;

  const handleSave = async (vehicleType: string, data: UpsertEmissionFactorRequest) => {
    try {
      setFormLoading(true);
      await emissionApi.upsert(vehicleType, data);
      showSuccess(t('common:emissionFactors.updateSuccess', 'Emissionsfaktor aktualisiert'));
      setShowForm(false);
      setEditing(null);
      await loadFactors();
    } catch (e) {
      showError(t('common:emissionFactors.saveError', 'Fehler beim Speichern'));
    } finally {
      setFormLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div style={{ marginBottom: '1rem' }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}><Leaf size={22} /> {t('common:emissionFactors.title', 'Emissionsfaktoren')}</h1>
        <p style={{ color: '#6b7280', margin: '0.25rem 0 0' }}>{t('common:emissionFactors.subtitle', 'CO₂e-Faktoren pro Verkehrsmittel verwalten')}</p>
      </div>

      {loading ? (
        <div className="page-loading"><div className="page-spinner" /></div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="page-table">
            <thead>
              <tr>
                <th>{t('common:emissionFactors.vehicleType', 'Verkehrsmittel')}</th>
                <th>{t('common:emissionFactors.gramsPerPassengerKm', 'g CO₂e / Pkm')}</th>
                <th>{t('common:emissionFactors.source', 'Quelle')}</th>
                <th>{t('common:status', 'Status')}</th>
                <th>{t('common:actions', 'Aktionen')}</th>
              </tr>
            </thead>
            <tbody>
              {EMISSION_VEHICLE_TYPES.map(vt => {
                const f = findFactor(vt.value);
                const missing = !f || !f.isActive;
                return (
                  <tr key={vt.value}>
                    <td>{vt.label}</td>
                    <td>
                      {f ? f.gramsCo2ePerPassengerKm : (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#d97706' }}>
                          <AlertTriangle size={14} /> {t('common:emissionFactors.missing', 'Nicht gesetzt')}
                        </span>
                      )}
                    </td>
                    <td>{f?.source || '—'}</td>
                    <td>
                      {missing ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#d97706' }}>
                          <AlertTriangle size={14} /> {t('common:emissionFactors.missing', 'Nicht gesetzt')}
                        </span>
                      ) : (
                        t('common:active', 'Aktiv')
                      )}
                    </td>
                    <td>
                      <button className="page-btn-icon" onClick={() => { setEditing({ value: vt.value, label: vt.label, factor: f }); setShowForm(true); }}>
                        <Pencil size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && editing && (
        <EmissionFactorForm
          vehicleTypeValue={editing.value}
          vehicleTypeLabel={editing.label}
          factor={editing.factor}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          loading={formLoading}
        />
      )}

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

export default EmissionFactors;
